/**
 * notify — единый фасад для toast-уведомлений поверх `sonner`.
 *
 * Назначение: централизовать вызовы тостов, чтобы:
 *  - не разносить импорты `toast` по 30 файлам;
 *  - иметь одно место для будущей локализации / форматирования сообщений;
 *  - выполнять дедупликацию повторяющихся ошибок (например, при шторме 5xx).
 */

import { toast } from 'sonner';

// Простая дедупликация: одинаковое сообщение не показываем повторно в течение окна.
const recent = new Map();
const DEDUPE_WINDOW_MS = 3000;

function shouldShow(key) {
    const now = Date.now();
    const last = recent.get(key);
    if (last && now - last < DEDUPE_WINDOW_MS) {
        return false;
    }
    recent.set(key, now);
    // Лёгкая чистка чтобы Map не рос бесконечно.
    if (recent.size > 50) {
        for (const [k, t] of recent) {
            if (now - t > DEDUPE_WINDOW_MS) recent.delete(k);
        }
    }
    return true;
}

function asText(message, fallback) {
    if (typeof message === 'string' && message.trim()) return message;
    if (message instanceof Error && message.message) return message.message;
    if (message && typeof message === 'object' && message.message) return String(message.message);
    return fallback;
}

export const notify = {
    success(message, options) {
        const text = asText(message, 'Готово');
        if (!shouldShow(`s:${text}`)) return;
        toast.success(text, options);
    },
    error(message, options) {
        const text = asText(message, 'Произошла ошибка');
        if (!shouldShow(`e:${text}`)) return;
        toast.error(text, options);
    },
    info(message, options) {
        const text = asText(message, '');
        if (!text) return;
        if (!shouldShow(`i:${text}`)) return;
        toast.info(text, options);
    },
    warn(message, options) {
        const text = asText(message, '');
        if (!text) return;
        if (!shouldShow(`w:${text}`)) return;
        toast.warning(text, options);
    },
    /** Универсальный wip-тост для функционала «в разработке». */
    wip(featureLabel) {
        const text = featureLabel
            ? `«${featureLabel}» — функция в разработке`
            : 'Функция в разработке';
        if (!shouldShow(`w:${text}`)) return;
        toast.warning(text);
    },
};

export default notify;
